import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  formatearMoneda,
  type EstadoCotizacion,
  type EstadoFactura,
  type EstadoProyecto,
} from "@uirtus/shared";
import { useState } from "react";
import { FormularioProyecto } from "../componentes/formulario-proyecto";
import { Aviso, Boton, Confirmacion, Etiqueta, Modal, Tarjeta } from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import { useActualizarProyecto, useEliminarProyecto, useProyecto } from "../lib/proyectos";

const ETIQUETA_ESTADO: Record<EstadoProyecto, string> = {
  PROPUESTA: "Propuesta",
  ACTIVO: "Activo",
  EN_PAUSA: "En pausa",
  COMPLETADO: "Completado",
  CANCELADO: "Cancelado",
};

const TONO_ESTADO: Record<EstadoProyecto, "neutro" | "exito" | "alerta" | "peligro"> = {
  PROPUESTA: "neutro",
  ACTIVO: "exito",
  EN_PAUSA: "alerta",
  COMPLETADO: "neutro",
  CANCELADO: "peligro",
};

const TONO_COTIZACION: Record<EstadoCotizacion, "neutro" | "exito" | "alerta" | "peligro"> = {
  BORRADOR: "neutro",
  ENVIADA: "alerta",
  ACEPTADA: "exito",
  RECHAZADA: "peligro",
  VENCIDA: "peligro",
  CANCELADA: "neutro",
};

const TONO_FACTURA: Record<EstadoFactura, "neutro" | "exito" | "alerta" | "peligro"> = {
  BORRADOR: "neutro",
  PENDIENTE_TIMBRE: "alerta",
  TIMBRADA: "exito",
  ERROR_TIMBRE: "peligro",
  CANCELACION_SOLICITADA: "alerta",
  CANCELADA: "peligro",
};

const formatearFecha = (iso: string) =>
  new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric" }).format(
    new Date(`${iso}T00:00:00`),
  );

export function ProyectoDetalle() {
  const { id } = useParams({ from: "/proyectos/$id" });
  // Mismo motivo que en la ficha de cotización: sin la key, navegar de un
  // proyecto a otro por la misma ruta reutilizaría el componente y arrastraría
  // el estado local (modales abiertos, avisos) del proyecto anterior.
  return <FichaProyecto key={id} id={id} />;
}

function FichaProyecto({ id }: { id: string }) {
  const navegar = useNavigate();
  const { data: p, isLoading, isError, error } = useProyecto(id);

  const [editando, setEditando] = useState(false);
  const [confirmarBaja, setConfirmarBaja] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const actualizar = useActualizarProyecto(id);
  const eliminar = useEliminarProyecto(id);

  if (isLoading) return <p className="text-sm text-slate-500">Cargando…</p>;

  if (isError || !p) {
    return (
      <div className="space-y-4">
        <Aviso>
          {error instanceof ErrorApi ? error.message : "No se pudo cargar el proyecto"}
        </Aviso>
        <Link to="/proyectos" className="text-sm text-marca-600 hover:underline">
          ← Volver a proyectos
        </Link>
      </div>
    );
  }

  const sinHistorial = p.cotizaciones.length === 0 && p.facturas.length === 0;

  return (
    <div className="space-y-5">
      <Link to="/proyectos" className="text-sm text-marca-600 hover:underline">
        ← Proyectos
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold">{p.nombre}</h1>
            <Etiqueta tono={TONO_ESTADO[p.estado]}>{ETIQUETA_ESTADO[p.estado]}</Etiqueta>
          </div>
          {p.clave && <p className="font-mono text-sm text-slate-500">{p.clave}</p>}
        </div>

        <div className="flex flex-wrap gap-2">
          <Boton variante="secundario" onClick={() => setEditando(true)}>
            Editar
          </Boton>
          <Boton
            variante="peligro"
            onClick={() => setConfirmarBaja(true)}
            disabled={!sinHistorial}
            title={sinHistorial ? undefined : "Tiene historial: cámbialo a Cancelado en su lugar"}
          >
            Eliminar
          </Boton>
        </div>
      </header>

      {mensaje && <Aviso tono="alerta">{mensaje}</Aviso>}

      <div className="grid gap-5 lg:grid-cols-2">
        <Tarjeta titulo="Cliente">
          <div className="space-y-1 text-sm">
            <Link
              to="/clientes/$id"
              params={{ id: p.clienteId }}
              className="font-medium text-marca-600 hover:underline dark:text-marca-500"
            >
              {p.cliente.razonSocial}
            </Link>
            <p className="font-mono text-xs text-slate-500">{p.cliente.rfc}</p>
          </div>
        </Tarjeta>

        <Tarjeta titulo="Datos del proyecto">
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Monto estimado</dt>
              <dd>
                {p.montoEstimado ? formatearMoneda(p.montoEstimado, p.moneda) : "—"}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Inicio</dt>
              <dd>{p.fechaInicio ? formatearFecha(p.fechaInicio) : "—"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Fin estimado</dt>
              <dd>{p.fechaFinEstimada ? formatearFecha(p.fechaFinEstimada) : "—"}</dd>
            </div>
            {p.fechaFinReal && (
              <div className="flex justify-between">
                <dt className="text-slate-500">Fin real</dt>
                <dd>{formatearFecha(p.fechaFinReal)}</dd>
              </div>
            )}
          </dl>
        </Tarjeta>
      </div>

      {p.descripcion && (
        <Tarjeta titulo="Descripción">
          <p className="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">
            {p.descripcion}
          </p>
        </Tarjeta>
      )}

      <Tarjeta titulo="Cotizaciones">
        {p.cotizaciones.length === 0 ? (
          <p className="py-2 text-center text-sm text-slate-500">
            Ninguna cotización ligada a este proyecto.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {p.cotizaciones.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <Link
                    to="/cotizaciones/$id"
                    params={{ id: c.id }}
                    className="font-medium text-marca-600 hover:underline dark:text-marca-500"
                  >
                    {c.folio}
                    {c.version > 1 && <span className="text-slate-400"> · v{c.version}</span>}
                  </Link>
                  <div className="text-xs text-slate-500">{c.titulo}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Etiqueta tono={TONO_COTIZACION[c.estado]}>{c.estado}</Etiqueta>
                  <span className="font-mono text-sm">{formatearMoneda(c.total, c.moneda)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>

      <Tarjeta titulo="Facturas">
        {p.facturas.length === 0 ? (
          <p className="py-2 text-center text-sm text-slate-500">
            Ninguna factura ligada a este proyecto.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {p.facturas.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 py-2">
                <span className="font-mono text-sm">
                  {f.serie && f.folio !== null ? `${f.serie}-${f.folio}` : "Sin folio"}
                </span>
                <div className="flex items-center gap-3">
                  <Etiqueta tono={TONO_FACTURA[f.estado]}>{f.estado}</Etiqueta>
                  <span className="font-mono text-sm">{formatearMoneda(f.total, f.moneda)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>

      <Modal
        titulo="Editar proyecto"
        abierto={editando}
        alCerrar={() => {
          setEditando(false);
          actualizar.reset();
        }}
      >
        <FormularioProyecto
          proyecto={p}
          clienteInicial={{ id: p.cliente.id, rfc: p.cliente.rfc, razonSocial: p.cliente.razonSocial }}
          enviando={actualizar.isPending}
          errorServidor={actualizar.error ? actualizar.error.message : null}
          alEnviar={(datos) =>
            actualizar.mutate(datos as never, {
              onSuccess: () => {
                setEditando(false);
                actualizar.reset();
              },
            })
          }
          alCancelar={() => {
            setEditando(false);
            actualizar.reset();
          }}
        />
      </Modal>

      <Confirmacion
        abierto={confirmarBaja}
        titulo="Eliminar proyecto"
        mensaje={
          <>
            Se va a borrar <strong>{p.nombre}</strong> de forma definitiva. Solo es posible
            porque no tiene cotizaciones ni facturas.
          </>
        }
        textoConfirmar="Eliminar"
        peligroso
        ocupado={eliminar.isPending}
        alCancelar={() => setConfirmarBaja(false)}
        alConfirmar={() =>
          eliminar.mutate(undefined, {
            onSuccess: () => void navegar({ to: "/proyectos" }),
            onError: (e) => {
              setConfirmarBaja(false);
              setMensaje(e.message);
            },
          })
        }
      />
    </div>
  );
}
